import { useEffect } from 'react'

import { useNavigate, useParams } from 'react-router-dom'

import { NAVIGATE_BACK_INDEX } from 'src/Constants/constants'
import { AdCampaignStrategyEnum } from 'src/Redux/ad-campaignApi'
import { useGetSpotifyTrackByIdQuery } from 'src/Redux/spotifyTracksApi'
import { useLazySearchPlaylistQuery } from 'src/Redux/search-playlistApi'
import { SearchedTrack } from 'src/Pages/Artist/CreateCampaignPage/Components/TrackSearch'
import { convertTimeHandler } from 'src/Constants/functions'

type RouteParams = {
  strategy: AdCampaignStrategyEnum
  resourceId: string
}

type UseMetaAdsResource = {
  resource: SearchedTrack | null
  loading: boolean
  strategy: AdCampaignStrategyEnum | undefined
}

export const useMetaAdsResource = (): UseMetaAdsResource => {
  const { strategy, resourceId } = useParams<RouteParams>()
  const navigate = useNavigate()

  const isPlaylist = strategy === AdCampaignStrategyEnum.PLAYLIST

  const {
    data: trackData,
    isLoading: isTrackLoading,
    isError: isTrackError,
  } = useGetSpotifyTrackByIdQuery(resourceId || '', {
    skip: !resourceId || isPlaylist,
  })

  const [
    getPlaylist,
    {
      data: playlistData,
      isLoading: isPlaylistLoading,
      isError: isPlaylistError,
    },
  ] = useLazySearchPlaylistQuery()

  useEffect(() => {
    if (isPlaylist && resourceId) {
      getPlaylist(resourceId)
    }
  }, [isPlaylist, resourceId, getPlaylist])

  useEffect(() => {
    if (isTrackError || isPlaylistError) {
      navigate(NAVIGATE_BACK_INDEX)
    }
  }, [isTrackError, isPlaylistError, navigate])

  let resource: SearchedTrack | null

  if (isPlaylist) {
    resource = playlistData || null
  } else if (trackData) {
    resource = {
      id: trackData.id,
      name: trackData.trackName,
      artistName: trackData.trackAuthor,
      trackCoverUrl: trackData.trackImage,
      previewUrl: trackData.previewUrl,
      trackDuration: convertTimeHandler({
        value: trackData?.trackDuration || 0,
        type: 'ms',
      }),
      resourceSpotifyUrl: trackData.resourceSpotifyUrl,
      ownerSpotifyUrl: trackData.artistSpotifyId,
    }
  } else {
    resource = null
  }

  return {
    resource,
    strategy,
    loading: isTrackLoading || isPlaylistLoading,
  }
}
